import React, { useContext, useEffect, useState } from "react";
import {
  FiHeart,
  FiMail,
  FiMenu,
  FiSearch,
  FiShoppingBag,
  FiUser,
  FiX,
} from "react-icons/fi";
import { Link, useLocation, useNavigate } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/header.scss";
import { CartContext } from "./CartContext";

const getMonogram = (brandName) =>
  brandName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

function Header() {
  const { cartCount, wishlistCount } = useContext(CartContext);
  const location = useLocation();
  const navigate = useNavigate();
  const currentSearch = new URLSearchParams(location.search);
  const [searchTerm, setSearchTerm] = useState(
    () => currentSearch.get("q") || currentSearch.get("search") || ""
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const contact = siteConfig.contact || {};
  const brandName = siteConfig.brandName;
  const logo = siteConfig.logo || {};
  const logoText = logo.value || siteConfig.logoText || brandName;
  const monogram = logo.monogram || getMonogram(brandName);

  useEffect(() => {
    const nextSearch = new URLSearchParams(location.search);
    setSearchTerm(nextSearch.get("q") || nextSearch.get("search") || "");
    setMobileMenuOpen(false);
    setMoreOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") setMobileMenuOpen(false);
    };

    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [mobileMenuOpen]);

  const submitSearch = (event) => {
    event.preventDefault();
    const query = searchTerm.trim();
    navigate(query ? `/shop?search=${encodeURIComponent(query)}` : "/shop");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const navigateFromHeader = () => {
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const isNavigationActive = (item) => {
    const [targetPath, targetQuery = ""] = item.path.split("?");
    if (location.pathname !== targetPath) return false;

    if (targetQuery) {
      const expected = new URLSearchParams(targetQuery);
      return Array.from(expected.entries()).every(
        ([key, value]) => currentSearch.get(key) === value
      );
    }

    if (targetPath === "/shop") {
      return !siteConfig.navigation.some((navigationItem) => {
        if (!navigationItem.path.includes("?")) return false;
        const [path, query] = navigationItem.path.split("?");
        if (path !== location.pathname) return false;
        const expected = new URLSearchParams(query);
        return Array.from(expected.entries()).every(
          ([key, value]) => currentSearch.get(key) === value
        );
      });
    }

    return true;
  };

  return (
    <header className="site-header">
      <div className="header-utility">
        <div className="header-shell header-utility__inner">
          <p>{siteConfig.announcement}</p>
          <nav className="header-utility__links" aria-label="Customer links">
            {contact.email && (
              <a href={`mailto:${contact.email}`}>
                <FiMail aria-hidden="true" />
                <span>{contact.email}</span>
              </a>
            )}
            <Link to="/account" onClick={navigateFromHeader}>
              <FiUser aria-hidden="true" />
              <span>Account</span>
            </Link>
            <Link to="/wishlist" onClick={navigateFromHeader}>
              <FiHeart aria-hidden="true" />
              <span>Wishlist</span>
              <span className="header-utility__count" aria-label={`${wishlistCount} saved items`}>
                {wishlistCount}
              </span>
            </Link>
            <Link to="/cartpage" onClick={navigateFromHeader}>
              <FiShoppingBag aria-hidden="true" />
              <span>Cart</span>
              <span className="header-utility__count" aria-label={`${cartCount} items in cart`}>
                {cartCount}
              </span>
            </Link>
          </nav>
        </div>
      </div>

      <div className="header-masthead">
        <div className="header-shell header-masthead__inner">
          <p className="header-masthead__aside header-masthead__aside--left">
            {logo.tagline || siteConfig.description}
          </p>

          <button
            className="header-mobile-toggle"
            type="button"
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-site-navigation"
            aria-label={mobileMenuOpen ? "Close navigation" : "Open navigation"}
            onClick={() => setMobileMenuOpen((isOpen) => !isOpen)}
          >
            {mobileMenuOpen ? <FiX aria-hidden="true" /> : <FiMenu aria-hidden="true" />}
          </button>

          <Link className="site-brand" to="/" onClick={navigateFromHeader}>
            {logo.type === "image" && logo.imagePath ? (
              <img className="site-brand__image" src={logo.imagePath} alt={`${brandName} home`} />
            ) : (
              <>
                <span className="site-brand__mark" aria-hidden="true"><span>{monogram}</span></span>
                <span className="site-brand__wordmark">{logoText}</span>
              </>
            )}
          </Link>

          <p className="header-masthead__aside header-masthead__aside--right">
            {siteConfig.announcement}
          </p>

          <Link
            className="header-mobile-cart"
            to="/cartpage"
            onClick={navigateFromHeader}
            aria-label={`Cart with ${cartCount} items`}
          >
            <FiShoppingBag aria-hidden="true" />
            <span>{cartCount}</span>
          </Link>
        </div>
      </div>

      <nav className="header-navigation" aria-label="Primary navigation">
        <ul className="header-shell">
          {siteConfig.navigation.map((item) => {
            const active = isNavigationActive(item);
            return (
              <li key={item.id}>
                <Link
                  className={active ? "is-active" : undefined}
                  to={item.path}
                  aria-current={active ? "page" : undefined}
                  onClick={navigateFromHeader}
                >
                  {item.label}
                </Link>
              </li>
            );
          })}
          <li className="header-more">
            <button type="button" aria-expanded={moreOpen} onClick={() => setMoreOpen((open) => !open)}>
              More <span aria-hidden="true">⌄</span>
            </button>
            {moreOpen && (
              <ul className="header-more__menu">
                {(siteConfig.legalNavigation || []).map((item) => (
                  <li key={item.id}><Link to={item.path} onClick={navigateFromHeader}>{item.label}</Link></li>
                ))}
              </ul>
            )}
          </li>
        </ul>
      </nav>

      <div
        className={`header-mobile-menu ${mobileMenuOpen ? "is-open" : ""}`}
        id="mobile-site-navigation"
        aria-hidden={!mobileMenuOpen}
      >
        <nav aria-label="Mobile navigation">
          <ul>
            {siteConfig.navigation.map((item) => {
              const active = isNavigationActive(item);
              return (
                <li key={item.id}>
                  <Link
                    className={active ? "is-active" : undefined}
                    to={item.path}
                    aria-current={active ? "page" : undefined}
                    onClick={navigateFromHeader}
                    tabIndex={mobileMenuOpen ? 0 : -1}
                  >
                    {item.label}
                  </Link>
                </li>
              );
            })}
            {(siteConfig.legalNavigation || []).map((item) => (
              <li key={`mobile-${item.id}`}><Link to={item.path} onClick={navigateFromHeader} tabIndex={mobileMenuOpen ? 0 : -1}>More · {item.label}</Link></li>
            ))}
          </ul>
        </nav>
        <div className="header-mobile-menu__account">
          <Link to="/account" onClick={navigateFromHeader} tabIndex={mobileMenuOpen ? 0 : -1}>
            <FiUser aria-hidden="true" /> Account
          </Link>
          <Link to="/wishlist" onClick={navigateFromHeader} tabIndex={mobileMenuOpen ? 0 : -1}>
            <FiHeart aria-hidden="true" /> Wishlist ({wishlistCount})
          </Link>
          {contact.email && (
            <a href={`mailto:${contact.email}`} tabIndex={mobileMenuOpen ? 0 : -1}>
              <FiMail aria-hidden="true" /> {contact.email}
            </a>
          )}
        </div>
      </div>

      <div className="header-shop-strip">
        <div className="header-shell header-shop-strip__inner">
          <span className="header-shop-strip__label">Find something delicious</span>
          <form className="header-search" role="search" onSubmit={submitSearch}>
            <label className="header-sr-only" htmlFor="site-search">
              Search products
            </label>
            <input
              id="site-search"
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search the pantry"
              autoComplete="off"
            />
            <button type="submit" aria-label="Search">
              <FiSearch aria-hidden="true" />
            </button>
          </form>
          <Link className="header-shop-strip__all" to="/shop" onClick={navigateFromHeader}>
            Shop all
            <span aria-hidden="true">→</span>
          </Link>
        </div>
      </div>

      <nav className="header-categories" aria-label="Product categories">
        <ul className="header-shell">
          {siteConfig.categories.map((category) => {
            const active =
              location.pathname === "/shop" &&
              (currentSearch.get("category") === category.id ||
                currentSearch.get("category") === category.name);
            return (
              <li key={category.id}>
                <Link
                  className={active ? "is-active" : undefined}
                  to={category.path}
                  aria-current={active ? "page" : undefined}
                  onClick={navigateFromHeader}
                >
                  {category.shortName || category.name}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </header>
  );
}

export default Header;
